import _ from 'lodash'
import { StateCreator, StoreMutatorIdentifier } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import { DraftingEditSuggestion } from 'openapi/models/DraftingEditSuggestion'
import { DraftingSummarizeAndCiteResponse } from 'openapi/models/DraftingSummarizeAndCiteResponse'
import { UploadedFile } from 'openapi/models/UploadedFile'

import { Playbook, StoredReviewResponse } from '../../../schemas/playbook'
import { DEFAULT_DOCUMENT_INSTRUCTION } from '../../../utils/constants'
import { PlaybookEditSuggestion } from '../utils/playbook-utils'

// Logger for Zustand store updates.
type Logger = <
  T,
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = [],
>(
  f: StateCreator<T, Mps, Mcs>,
  name?: string
) => StateCreator<T, Mps, Mcs>

type LoggerImpl = <T>(
  f: StateCreator<T, [], []>,
  name?: string
) => StateCreator<T, [], []>

const computeShallowDiff = (
  prevState: Record<string, unknown>,
  newState: Record<string, unknown>
) => {
  return Object.entries(prevState).reduce(
    (diff, [key, value]) =>
      _.isEqual(newState[key], value)
        ? diff
        : { ...diff, [key]: { old: value, new: newState[key] } },
    {} as Record<string, { old: unknown; new: unknown }>
  )
}

const loggerImpl: LoggerImpl = (f, name) => (set, get, store) => {
  if (process.env.NODE_ENV !== 'development') {
    return f(set, get, store)
  }
  const loggedSet: typeof set = (...a) => {
    const prevState = get()
    set(...(a as Parameters<typeof set>))
    const newState = get()
    const diff = computeShallowDiff(
      prevState as Record<string, unknown>,
      newState as Record<string, unknown>
    )
    if (Object.keys(diff).length > 0) {
      console.group(name || 'State Update')
      console.info('Changed values:', diff)
      console.groupEnd()
    }
  }
  const setState = store.setState
  store.setState = (...a) => {
    const prevState = store.getState()
    setState(...(a as Parameters<typeof setState>))
    const newState = store.getState()
    const diff = Object.entries(prevState as Record<string, unknown>).reduce(
      (diff, [key, value]) =>
        _.isEqual((newState as Record<string, unknown>)[key], value)
          ? diff
          : { ...diff, [key]: value },
      {} as Record<string, unknown>
    )
    if (Object.keys(diff).length > 0) {
      console.group(name || 'State Update')
      console.info('Changed values:', diff)
      console.groupEnd()
    }
  }

  return f(loggedSet, get, store)
}

export const logger = loggerImpl as unknown as Logger

export interface SetHighlightProps {
  highlightedText: Word.Range | null
}

export interface DraftingEditSuggestionExtended extends DraftingEditSuggestion {
  range: Word.Range
  originalText: string
  dirty?: boolean
}

export interface QueryError {
  reason: string
}

export enum FileStatus {
  UPLOADING = 'uploading',
  UPLOADED = 'uploaded',
  FAILED = 'failed',
}
export interface FileState {
  name: string
  status: FileStatus
  uploadedFile: UploadedFile | null
  selected: boolean
  abortController: AbortController
}

interface AppState {
  documentId: string
  eventId: number
  reviewSuggestions: boolean
  isFetching: boolean
  isApplyingSuggestion: boolean
  hasAddedFilesToUpload: boolean
  isDropzoneLoading: boolean
  instruction: string
  queryCompleted: boolean
  queryId: string
  acceptedSuggestions: number[]
  suggestions: DraftingEditSuggestionExtended[]
  editSummary: DraftingSummarizeAndCiteResponse | null
  files: FileState[]
  highlightedText: Word.Range | null
  queryError: QueryError | null
  trackedRanges: Word.Range[]
  reviewResult: StoredReviewResponse | null
  playbook: Playbook | null
}

interface AppActions {
  setDocumentId: (documentId: string) => void
  setEventId: (eventId: number) => void
  setHighlightedText: ({ highlightedText }: SetHighlightProps) => void
  unTrackHighlightedText: () => void
  setInstruction: (instruction: string) => void
  setQueryId: (queryId: string) => void
  setIsFetching: (isFetching: boolean) => void
  setQueryCompleted: (completed: boolean) => void
  setSuggestions: (
    suggestions: DraftingEditSuggestionExtended[],
    clearAccepted?: boolean
  ) => void
  addSuggestion: (suggestion: DraftingEditSuggestionExtended) => void
  addAcceptedSuggestion: (suggestion: number) => void
  clearAcceptedSuggestions: () => void
  dismissSuggestion: (suggestion: number) => void
  setIsApplyingSuggestion: (isApplyingSuggestions: boolean) => void
  setReviewSuggestions: (reviewSuggestions: boolean) => void
  setHasAddedFilesToUpload: (hasAddedFilesToUpload: boolean) => void
  addUploadingFiles: (uploadingFiles: FileState[]) => void
  setFileUploaded: (uploadedFile: UploadedFile) => void
  setFileFailed: (filename: string) => void
  markFileSelected: (filename: string, selected: boolean) => void
  removeFile: (filename: string) => void
  setIsDropzoneLoading: (isDropzoneLoading: boolean) => void
  setQueryError: (queryError: QueryError | null) => void
  setEditSummary: (editSummary: DraftingSummarizeAndCiteResponse) => void
  setReviewResult: (reviewResult: StoredReviewResponse | null) => void
  markRuleResolved: (ruleName: string) => void
  setPlaybook: (playbook: Playbook | null) => void
  addSuggestionsForReference: (
    ruleName: string,
    referenceId: string,
    suggestions: PlaybookEditSuggestion[]
  ) => void
  markSuggestionApplied: (
    ruleName: string,
    referenceId: string,
    suggestionId: string | null
  ) => void
  markReferenceHighlighted: (
    ruleName: string,
    referenceId: string | null
  ) => void
  setRangeForReference: (
    ruleName: string,
    referenceId: string,
    range: Word.Range
  ) => void
  resetServerState: () => void
}

const appInitialState = {
  documentId: '',
  eventId: 0,
  reviewSuggestions: true,
  useSources: false,
  highlightedText: null,
  instruction: '',
  queryId: '',
  isFetching: false,
  queryCompleted: false,
  suggestions: [],
  editSummary: null,
  acceptedSuggestions: [],
  isApplyingSuggestion: false,
  hasAddedFilesToUpload: false,
  files: [],
  isDropzoneLoading: false,
  queryError: null,
  trackedRanges: [],
  reviewResult: null,
  playbook: null,
  referenceRanges: {},
}

export const AppStore = logger(
  immer<AppState & AppActions>((set) => ({
    ...appInitialState,
    setDocumentId: (documentId: string) => set({ documentId }),
    setEventId: (eventId: number) => set({ eventId }),
    setHighlightedText: ({ highlightedText }: SetHighlightProps) =>
      set((state) => {
        // Store the range object for eventual cleanup.
        if (state.highlightedText) {
          state.trackedRanges.push(state.highlightedText)
        }
        state.highlightedText = highlightedText
      }),
    unTrackHighlightedText: () =>
      set((state) => {
        state.highlightedText?.untrack()
        state.highlightedText = null
      }),
    setInstruction: (instruction: string) =>
      set(() => ({
        instruction,
      })),
    setQueryId: (queryId: string) => set({ queryId }),
    setQueryError: (queryError: QueryError | null) => set({ queryError }),
    setIsFetching: (isFetching: boolean) => set({ isFetching }),
    setQueryCompleted: (completed: boolean) =>
      set({ queryCompleted: completed }),
    setSuggestions: (
      suggestions: DraftingEditSuggestionExtended[],
      clearAccepted: boolean = false
    ) =>
      set((state) => {
        if (clearAccepted) {
          state.acceptedSuggestions = []
        }
        const idxOfSuggestion = state.suggestions.findIndex(
          (suggestion, idx) =>
            suggestion.suggestion !== suggestions[idx].suggestion
        )
        state.acceptedSuggestions = state.acceptedSuggestions.filter(
          (acceptedSuggestion) => acceptedSuggestion !== idxOfSuggestion
        )
        state.suggestions = suggestions
      }),
    addSuggestion: (suggestion: DraftingEditSuggestionExtended) =>
      set((state) => {
        state.suggestions.push(suggestion)
      }),
    dismissSuggestion: (suggestion: number) =>
      set((state) => {
        state.suggestions.splice(suggestion, 1)
      }),
    addAcceptedSuggestion: (suggestion: number) =>
      set((state) => {
        state.acceptedSuggestions.push(suggestion)
      }),
    clearAcceptedSuggestions: () =>
      set((state) => {
        state.acceptedSuggestions = []
      }),
    setIsApplyingSuggestion: (isApplyingSuggestion: boolean) =>
      set({ isApplyingSuggestion }),
    setReviewSuggestions: (reviewSuggestions: boolean) =>
      set({ reviewSuggestions }),
    setHasAddedFilesToUpload: (hasAddedFilesToUpload: boolean) =>
      set({ hasAddedFilesToUpload }),
    addUploadingFiles: (uploadingFiles: FileState[]) =>
      set((state) => {
        if (!state.instruction && state.highlightedText?.text?.length) {
          state.instruction = DEFAULT_DOCUMENT_INSTRUCTION
        }
        state.files = [...state.files, ...uploadingFiles]
      }),
    setFileUploaded: (uploadedFile: UploadedFile) =>
      set((state) => {
        const file = state.files.find((file) => file.name === uploadedFile.name)
        if (file) {
          file.status = FileStatus.UPLOADED
          file.uploadedFile = uploadedFile
          file.selected = true
        }
      }),
    setFileFailed: (filename: string) =>
      set((state) => {
        const file = state.files.find((file) => file.name === filename)
        if (file) {
          file.status = FileStatus.FAILED
        }
      }),
    markFileSelected: (filename: string, selected: boolean) =>
      set((state) => {
        const file = state.files.find((file) => file.name === filename)
        if (file) {
          file.selected = selected
        }
      }),
    removeFile: (filename: string) =>
      set((state) => {
        state.files = state.files.filter((file) => file.name !== filename)
      }),
    setIsDropzoneLoading: (isDropzoneLoading: boolean) =>
      set({ isDropzoneLoading }),
    setEditSummary: (editSummary: DraftingSummarizeAndCiteResponse) =>
      set({ editSummary }),
    setReviewResult: (reviewResult: StoredReviewResponse | null) =>
      set({ reviewResult }),
    markRuleResolved: (ruleName: string) =>
      set((state) => {
        const rule = state.reviewResult?.review.find(
          (rule) => rule.ruleName === ruleName
        )
        if (rule) {
          rule.resolved = true
        }
      }),
    setPlaybook: (playbook: Playbook | null) => set({ playbook }),
    addSuggestionsForReference: (
      ruleName: string,
      referenceId: string,
      suggestions: PlaybookEditSuggestion[]
    ) =>
      set((state) => {
        const rule = state.reviewResult?.review.find(
          (rule) => rule.ruleName === ruleName
        )
        if (rule) {
          const reference = rule.references.find(
            (reference) => reference.id === referenceId
          )
          if (reference) {
            reference.suggestions = [
              ...(reference.suggestions || []),
              ...suggestions,
            ]
          }
        }
      }),
    markSuggestionApplied: (
      ruleName: string,
      referenceId: string,
      suggestionId: string | null
    ) =>
      set((state) => {
        const rule = state.reviewResult?.review.find(
          (rule) => rule.ruleName === ruleName
        )
        if (rule) {
          rule.highlightedReferenceId = referenceId
          const reference = rule.references.find(
            (reference) => reference.id === referenceId
          )
          if (reference) {
            reference.appliedSuggestionId = suggestionId
          }
        }
      }),
    markReferenceHighlighted: (ruleName: string, referenceId: string | null) =>
      set((state) => {
        const rule = state.reviewResult?.review.find(
          (rule) => rule.ruleName === ruleName
        )
        if (rule) {
          rule.highlightedReferenceId = referenceId
        }
      }),
    setRangeForReference: (
      ruleName: string,
      referenceId: string,
      range: Word.Range
    ) =>
      set((state) => {
        const rule = state.reviewResult?.review.find(
          (rule) => rule.ruleName === ruleName
        )
        if (rule) {
          const reference = rule.references.find(
            (reference) => reference.id === referenceId
          )
          if (reference) {
            reference.range?.untrack()
            reference.range = range
          }
        }
      }),
    resetServerState: () =>
      set((state) => {
        state.trackedRanges.forEach((range) => {
          range.untrack()
        })
        // Clean up reference ranges
        Object.values(state.reviewResult?.review || []).forEach((rule) => {
          Object.values(rule.references || []).forEach((reference) => {
            if (reference.range) {
              reference.range.untrack()
            }
          })
        })
        state.suggestions = []
        state.acceptedSuggestions = []
        state.isApplyingSuggestion = false
        state.editSummary = null
        state.queryError = null
        state.queryCompleted = false
        state.trackedRanges = []
        state.reviewResult = null
        state.playbook = null
      }),
  }))
)

export default AppStore
